import { useState, useMemo } from 'react';
import { Card, Col, Row, Form, Button, Spinner } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart, LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Box, Chip, Button as MuiButton } from '@mui/material';
import VersionHistory, { VersionItem } from '../../../../components/common/VersionHistory';
import { useAppContext } from 'providers/AppProvider';
import { getClientCredentials } from 'app/shared/utils/cookie.utils';

echarts.use([TooltipComponent, GridComponent, BarChart, LineChart, CanvasRenderer]);

const MATCH_THRESHOLD = 0.75;
const DEEPFAKE_THRESHOLD = 0.6;
const COMPLIANCE_THRESHOLD = 0.7;

export interface AudioAnalysisResult {
  file: string;
  timestamp?: string;
  claimed_speaker: string;
  speaker_scores: Array<{ speaker: string; mean_score: number; std_score?: number }>;
  best_match: { speaker: string; mean_score: number; std_score?: number };
  match_status: string;
  deepfake_risk: number;
  decision: string;
  policy_violations: string[];
  policy_violation_score: number;
  compliance_score: number;
  transcript?: string;
}

/** One item from the API results array (multiple audio_deepfake records) */
export interface AudioResultItem {
  analysis: AudioAnalysisResult;
  file_name?: string;
  file_url?: string;
  upload_id?: string;
}

interface AudioTabProps {
  projectId: string;
  userId: string;
  /** @deprecated No longer used – endpoint analyzes all project uploads without URL */
  initialAudioUrl?: string;
  /** @deprecated No longer used */
  audioUploads?: Array<{ file_url: string; title?: string; file_name?: string }>;
}

const AudioTab = ({ projectId, userId }: AudioTabProps) => {
  const { getThemeColor } = useAppContext();
  const [claimedSpeaker, setClaimedSpeaker] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Full API response: multiple audio_deepfake records (versions) */
  const [apiResponse, setApiResponse] = useState<{ results: AudioResultItem[] } | null>(null);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);

  const results = apiResponse?.results ?? [];
  const selectedItem = results[selectedVersionIndex];
  /** Analysis for the selected version, with file name from result item */
  const result = useMemo((): AudioAnalysisResult | null => {
    if (!selectedItem?.analysis) return null;
    const a = selectedItem.analysis;
    return { ...a, file: a.file || selectedItem.file_name || 'Unknown' };
  }, [selectedItem]);

  const getVersionId = (item: AudioResultItem, index: number) => item.upload_id ?? item.file_name ?? `index-${index}`;

  const versionItems: VersionItem[] = useMemo(
    () =>
      results.map((r, i) => {
        const ts = r.analysis?.timestamp ?? '';
        return {
          id: getVersionId(r, i),
          timestamp: ts,
          date: ts,
          modifier: 'System',
          isCurrent: i === selectedVersionIndex,
          title: r.file_name ?? `Version ${i + 1}`
        };
      }),
    [results, selectedVersionIndex]
  );

  const handleVersionHistorySelect = (versionItem: VersionItem) => {
    const idx = results.findIndex((r, i) => getVersionId(r, i) === versionItem.id);
    if (idx >= 0) setSelectedVersionIndex(idx);
  };

  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const runAnalysis = async () => {
    if (!projectId || !userId) {
      setError('Project and user are required. Please select a project and ensure you are logged in.');
      return;
    }
    setError(null);
    setApiResponse(null);
    setLoading(true);
    try {
      const body = {
        projectId,
        userId,
        claimed_speaker: claimedSpeaker.trim() || 'unknown'
      };

      const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
      const url = `${baseUrl}/project/features/audio-deepfake-analysis`;
      const environment = (localStorage.getItem('SELECTED_ENV') || 'DEV') as 'DEV' | 'PROD';
      const credentials = getClientCredentials(environment);
      const token = localStorage.getItem('accessToken');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (credentials?.clientId) headers['X-Client-Id'] = credentials.clientId;
      if (credentials?.clientSecret) headers['X-Client-Secret'] = credentials.clientSecret;
      headers['X-User-Id'] = userId;

      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = (data as any)?.message ?? (data as any)?.error ?? res.statusText ?? 'Analysis failed';
        throw new Error(message);
      }
      // API returns { audio_deepfake: { data: [{ analysis, file_name, file_url, upload_id }, ...], ... }, analyzed_count, message, ... }
      const rawData = (data as any)?.audio_deepfake?.data;
      const rawResults = Array.isArray(rawData)
        ? rawData
        : Array.isArray((data as any)?.results)
        ? (data as any).results
        : [];
      const results: AudioResultItem[] = rawResults.map((r: any) => ({
        analysis: r?.analysis ?? r,
        file_name: r?.file_name,
        file_url: r?.file_url,
        upload_id: r?.upload_id
      }));
      setApiResponse({ results });
      setSelectedVersionIndex(0);
    } catch (err: any) {
      const message = err?.message || 'Analysis failed';
      setError(message);
      setApiResponse(null);
    } finally {
      setLoading(false);
    }
  };

  if (!result) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <h4 className="mb-4">Audio Evaluation</h4>
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <p className="text-body-secondary mb-4">
              Run deepfake analysis on all audio uploads for this project. No URL or file needed.
            </p>
            {(!projectId || !userId) && (
              <div className="alert alert-warning mb-4" role="alert">
                Select a project and ensure you are logged in to run analysis.
              </div>
            )}
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Claimed speaker (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. test, john"
                    value={claimedSpeaker}
                    onChange={e => setClaimedSpeaker(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            {error && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {error}
              </div>
            )}
            <div className="mt-4">
              <Button
                id="audio-deepfake-run-analysis-btn"
                type="button"
                variant="phoenix-primary"
                disabled={loading}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  runAnalysis();
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Analyzing...
                  </>
                ) : (
                  'Run'
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  const safeNum = (v: unknown): number => {
    const n = Number(v);
    return typeof n === 'number' && !Number.isNaN(n) && isFinite(n) ? n : 0;
  };
  const risk = safeNum(result.deepfake_risk);
  const compliance = safeNum(result.compliance_score);
  const policyScore = safeNum(result.policy_violation_score);

  // Chart 1: Audio Evaluation (Voice Match, Risk, Compliance per speaker + threshold lines)
  const speakerScores = Array.isArray(result.speaker_scores) ? result.speaker_scores : [];
  const speakers =
    speakerScores.length > 0
      ? speakerScores.map(s => String(s.speaker || 'Speaker'))
      : [result.claimed_speaker || 'Speaker'];
  const matchScores =
    speakerScores.length > 0 ? speakerScores.map(s => safeNum(s.mean_score)) : [safeNum(result.best_match?.mean_score)];
  const bestSpeaker =
    result.best_match?.speaker != null && String(result.best_match.speaker).trim() !== ''
      ? String(result.best_match.speaker)
      : speakers[0];
  const riskValues = speakers.map(s => (s === bestSpeaker ? risk : 0));
  const complianceValues = speakers.map(s => (s === bestSpeaker ? compliance : 0));

  const evaluationOptions = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1
    },
    legend: { top: 0, right: 0 },
    xAxis: {
      type: 'category' as const,
      data: speakers,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor }
    },
    yAxis: {
      type: 'value' as const,
      min: 0,
      max: 1.2,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      splitLine: { show: false }
    },
    series: [
      { name: 'Voice Match', type: 'bar', data: matchScores, itemStyle: { color: '#22c55e' }, barWidth: '25%' },
      { name: 'Risk', type: 'bar', data: riskValues, itemStyle: { color: '#ef4444' }, barWidth: '25%' },
      { name: 'Compliance', type: 'bar', data: complianceValues, itemStyle: { color: '#3b82f6' }, barWidth: '25%' },
      {
        name: 'Match threshold (0.75)',
        type: 'line',
        data: speakers.map(() => MATCH_THRESHOLD),
        symbol: 'none',
        lineStyle: { type: 'dashed', color: '#22c55e', width: 1 },
        showInLegend: false
      },
      {
        name: 'Deepfake threshold (0.6)',
        type: 'line',
        data: speakers.map(() => DEEPFAKE_THRESHOLD),
        symbol: 'none',
        lineStyle: { type: 'dashed', color: '#ef4444', width: 1 },
        showInLegend: false
      },
      {
        name: 'Compliance threshold (0.7)',
        type: 'line',
        data: speakers.map(() => COMPLIANCE_THRESHOLD),
        symbol: 'none',
        lineStyle: { type: 'dashed', color: '#3b82f6', width: 1 },
        showInLegend: false
      }
    ],
    grid: { left: '10%', right: '10%', bottom: '15%', top: '15%' }
  };

  // Chart 2: Risk Summary (Policy, Risk, Compliance)
  const summaryOptions = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1
    },
    xAxis: {
      type: 'category' as const,
      data: ['Policy', 'Risk', 'Compliance'],
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor }
    },
    yAxis: {
      type: 'value' as const,
      min: 0,
      max: 1.2,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor }
    },
    series: [
      {
        type: 'bar' as const,
        data: [
          { value: policyScore, itemStyle: { color: '#f97316' } },
          { value: risk, itemStyle: { color: '#ef4444' } },
          { value: compliance, itemStyle: { color: '#3b82f6' } }
        ],
        barWidth: '50%'
      }
    ],
    grid: { left: '15%', right: '10%', bottom: '15%', top: '10%' }
  };

  return (
    <div style={{ padding: '24px 32px' }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <h4 className="mb-0">Audio Evaluation</h4>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {results.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`${results.length} version${results.length !== 1 ? 's' : ''}`}
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 500,
                  borderColor: 'primary.main',
                  color: 'primary.main'
                }}
              />
              <MuiButton
                onClick={() => setHistoryOpen(true)}
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                  }
                }}
              >
                View All
              </MuiButton>
            </Box>
          )}
          <Button variant="outline-primary" size="sm" onClick={() => setApiResponse(null)}>
            Run another analysis
          </Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={12} md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <h6 className="text-uppercase text-muted fw-semibold mb-3">Result</h6>
              <ul className="list-unstyled small mb-0">
                <li>
                  <strong>File:</strong> {result.file}
                </li>
                <li>
                  <strong>Claimed speaker:</strong> {result.claimed_speaker}
                </li>
                <li>
                  <strong>Best match:</strong> {result.best_match?.speaker} (
                  {safeNum(result.best_match?.mean_score).toFixed(4)})
                </li>
                <li>
                  <strong>Match status:</strong> {result.match_status}
                </li>
                <li>
                  <strong>Decision:</strong> {result.decision}
                </li>
                <li>
                  <strong>Deepfake risk:</strong> {risk.toFixed(4)}
                </li>
                <li>
                  <strong>Compliance score:</strong> {compliance.toFixed(4)}
                </li>
                <li>
                  <strong>Policy violation score:</strong> {policyScore.toFixed(4)}
                </li>
                {result.policy_violations?.length ? (
                  <li>
                    <strong>Policy violations:</strong> {result.policy_violations.join(', ')}
                  </li>
                ) : null}
              </ul>
            </Card.Body>
          </Card>
        </Col>
        {result.transcript ? (
          <Col xs={12} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <h6 className="text-uppercase text-muted fw-semibold mb-3">Transcript</h6>
                <p className="small mb-0 text-break">{result.transcript}</p>
              </Card.Body>
            </Card>
          </Col>
        ) : null}
      </Row>

      <Row className="g-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3">Audio Evaluation</Card.Title>
              <div style={{ height: '320px', width: '100%', minWidth: 0 }}>
                <ReactEChartsCore
                  echarts={echarts}
                  option={evaluationOptions}
                  style={{ height: '320px', width: '100%' }}
                  notMerge
                />
              </div>
              <p className="small text-muted mt-2 mb-0">
                Green dashed: match threshold (0.75). Red dashed: deepfake threshold (0.6). Blue dashed: compliance
                threshold (0.7).
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3">Risk Summary</Card.Title>
              <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
                <ReactEChartsCore
                  echarts={echarts}
                  option={summaryOptions}
                  style={{ height: '280px', width: '100%' }}
                  notMerge
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <VersionHistory
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        versions={versionItems}
        onVersionSelect={handleVersionHistorySelect}
        title="Version History - Audio"
      />
    </div>
  );
};

export default AudioTab;
